import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import type {
  AuthSessionResponse,
  OnboardingState,
  WorkspaceTeamBlueprint,
} from "@yagni/shared";
import { AppShell } from "./shell/AppShell.js";
import { PageTransition } from "./shell/PageTransition.js";
import { FeedbackSidePanel } from "./components/FeedbackSidePanel.js";
import { CreateWorkspacePage } from "./pages/CreateWorkspacePage.js";
import { InviteOnboarding } from "./pages/InviteOnboarding.js";
import { GodModeBanner } from "./components/GodModeBanner.js";
import { useWebSocket } from "./hooks/useWebSocket.js";
import { useReportTimezone } from "./hooks/useReportTimezone.js";
import { useWelcomeRedirect } from "./onboarding/useWelcomeRedirect.js";
import { useUiContextReporter } from "./hooks/useUiContextReporter.js";
import { resolveFeatureFlag } from "./hooks/useFeatureFlag.js";
import { TooltipProvider } from "./ui/tooltip.js";
import { Toaster } from "./ui/toast.js";
import { identifyUser, resetUser } from "./analytics/index.js";
import { usePageViewTracking } from "./hooks/usePageViewTracking.js";
import { AUTHENTICATED_DEFAULT_PATH } from "./routes/defaults.js";
import { fetchWithRetry } from "./lib/fetchWithRetry.js";
import { captureUiError } from "./sentry.js";
import { setDatadogRumUser } from "./datadogRum.js";
import {
  CompanyTeamOnboarding,
  shouldShowCompanyTeamOnboarding,
} from "./pages/CompanyTeamOnboarding.js";

type OnboardingLoadState = "idle" | "loading" | "ready" | "error";

interface AuthResponse {
  session: AuthSessionResponse;
  token: {
    value: string;
    expiresAt: string;
  };
}

type AuthState = "loading" | "signed-out" | "signed-in";

function ignoreAppSocketEvents(): void {}

export function App() {
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [authState, setAuthState] = useState<AuthState>("loading");

  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  const [onboardingLoadState, setOnboardingLoadState] = useState<OnboardingLoadState>("idle");
  const [godModeExiting, setGodModeExiting] = useState(false);
  const [godModeToggling, setGodModeToggling] = useState(false);
  // Native feedback panel (opened from the account menu's "Help & feedback"
  // entry, gated by feedback.panel.enabled). Persisted to localStorage so a
  // bug report the user was mid-typing survives a reload.
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("yagni:feedback-panel:open") === "true";
  });
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isNewWorkspaceFlow = location.pathname === "/onboarding" && searchParams.get("new") === "true";
  const activeWorkspaceId = session?.workspace.id;
  const activeRole = session?.memberships.find(
    (membership) => membership.workspace.id === activeWorkspaceId,
  )?.role ?? "member";

  const loadSession = useCallback(async () => {
    // Retry 429s and transient 5xx (deploy/restart/failover blips) with backoff
    // so a brief backend hiccup doesn't surface as "Failed to load session".
    const response = await fetchWithRetry("/api/auth/session");
    if (response.status === 401) {
      setSession(null);
      setAuthState("signed-out");
      return null;
    }
    if (!response.ok) {
      throw new Error("Failed to load session");
    }

    const payload = (await response.json()) as AuthSessionResponse;
    setSession(payload);
    setAuthState("signed-in");
    return payload;
  }, []);

  useEffect(() => {
    loadSession().catch((error) => {
      captureUiError(error, {
        area: "app",
        operation: "load_session",
        route: window.location.pathname,
      });
      setAuthState("signed-out");
    });
  }, [loadSession, location.pathname]);

  useEffect(() => {
    Sentry.setTag("route", location.pathname);
  }, [location.pathname]);

  // Keep the Sentry user scope in sync with the signed-in session so
  // every captured error and performance transaction is tagged with the
  // current user/workspace. No-op when Sentry is not initialized.
  useEffect(() => {
    if (authState === "signed-in" && session) {
      Sentry.setUser({ id: session.user.id });
      Sentry.setTag("workspace_id", session.workspace.id);
      setDatadogRumUser({ id: session.user.id, workspaceId: session.workspace.id });
      identifyUser(session);
    } else if (authState === "signed-out") {
      Sentry.setUser(null);
      setDatadogRumUser(null);
      resetUser();
    }
  }, [authState, session]);

  usePageViewTracking(authState === "signed-in");

  const loadOnboarding = useCallback(async (workspaceId: string) => {
    setOnboardingLoadState("loading");
    try {
      const response = await fetchWithRetry("/api/workspace/onboarding");
      if (!response.ok) throw new Error("Failed to load workspace onboarding");
      setOnboarding((await response.json()) as OnboardingState);
      setOnboardingLoadState("ready");
    } catch (error) {
      // Fail open: a failed load must never block the home surface. Retain a
      // previously loaded plan on a transient failure; on first load, leave
      // `onboarding` null and render the app normally (`/team-room/setup`
      // falls through to its null-blueprint redirect to Work).
      setOnboardingLoadState("error");
      captureUiError(error, {
        area: "onboarding",
        operation: "load_workspace_onboarding",
        route: window.location.pathname,
        workspaceId,
      });
    }
  }, []);

  useEffect(() => {
    if (authState !== "signed-in" || !session) {
      setOnboarding(null);
      setOnboardingLoadState("idle");
      return;
    }
    setOnboarding(null);
    void loadOnboarding(session.workspace.id);
  }, [authState, loadOnboarding, session?.workspace.id]);

  useEffect(() => {
    const blueprint = onboarding?.teamBlueprint;
    if (
      onboardingLoadState === "ready" &&
      location.pathname === AUTHENTICATED_DEFAULT_PATH &&
      !session?.godMode &&
      (activeRole === "owner" || activeRole === "admin") &&
      blueprint &&
      (blueprint.status === "draft" || blueprint.status === "failed" || blueprint.status === "approved")
    ) {
      navigate("/team-room/setup", { replace: true });
    }
  }, [activeRole, location.pathname, navigate, onboarding?.teamBlueprint, onboardingLoadState, session?.godMode]);

  const handleGodModeExit = useCallback(async () => {
    setGodModeExiting(true);
    try {
      const response = await fetch("/api/auth/god-mode/exit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error("Failed to exit god mode");
      }
      const payload = (await response.json()) as AuthResponse;
      setSession(payload.session);
      window.location.href = "/admin/workspaces";
    } catch (error) {
      captureUiError(error, {
        area: "auth",
        operation: "exit_god_mode",
        route: location.pathname,
        workspaceId: session?.workspace.id,
      });
    } finally {
      setGodModeExiting(false);
    }
  }, [location.pathname, session?.workspace.id]);

  const handleGodModeToggleWrite = useCallback(
    async (enabled: boolean) => {
      // Enabling Act mode is confirmed in the banner itself (GodModeBanner), not
      // with window.confirm: a native dialog freezes the page for browser
      // automation driving an operator session.
      setGodModeToggling(true);
      try {
        const response = await fetch("/api/auth/god-mode/act", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ enabled }),
        });
        if (response.status === 401) {
          const body = (await response.json().catch(() => ({}))) as {
            error?: string;
          };
          if (body.error === "mfa_required") {
            // Re-verify with Google before unlocking writes, then come back to
            // the current view (still in god mode, Observe) to re-enable.
            const returnTo = `${window.location.pathname}${window.location.search}`;
            window.location.href = `/api/auth/google/step-up?returnTo=${encodeURIComponent(returnTo)}`;
            return;
          }
          throw new Error("Failed to toggle act mode");
        }
        if (!response.ok) {
          throw new Error("Failed to toggle act mode");
        }
        const payload = (await response.json()) as AuthResponse;
        // Only the godMode flag changed. Merge it instead of replacing the
        // whole session so featureFlags (which gate the Triptych shell) and
        // memberships survive — the act endpoint's AuthResult omits them.
        setSession((prev) =>
          prev ? { ...prev, godMode: payload.session.godMode } : payload.session
        );
      } catch (error) {
        captureUiError(error, {
          area: "auth",
          operation: "toggle_god_mode_write",
          route: location.pathname,
          workspaceId: session?.workspace.id,
        });
      } finally {
        setGodModeToggling(false);
      }
    },
    [location.pathname, session?.workspace.id]
  );

  const handleSignOut = useCallback(async () => {
    try {
      await fetch("/api/auth/sign-out", {
        method: "POST",
      });
    } finally {
      // Navigate to the landing page (Astro serves / in both dev and prod)
      window.location.href = "/";
    }
  }, []);

  const handleWorkspaceCreated = useCallback(() => {
    // New workspace was created + session switched — full reload picks up the
    // new context (session and onboarding rekey to the workspace).
    window.location.href = AUTHENTICATED_DEFAULT_PATH;
  }, []);

  const handleInviteOnboardingComplete = useCallback(() => {
    setOnboarding((prev) =>
      prev ? { ...prev, introCompleted: true } : prev
    );
    navigate(AUTHENTICATED_DEFAULT_PATH);
  }, [navigate]);

  const handleTeamBlueprintChange = useCallback((teamBlueprint: WorkspaceTeamBlueprint) => {
    setOnboarding((current) => current ? { ...current, teamBlueprint } : current);
  }, []);

  const handleTeamBlueprintDeferred = useCallback((teamBlueprint: WorkspaceTeamBlueprint) => {
    setOnboarding((current) => current ? { ...current, teamBlueprint } : current);
    // A deferred plan returns to Work; the query is deliberately cleared so a
    // deferred plan never becomes a fresh global hold.
    navigate(AUTHENTICATED_DEFAULT_PATH, { replace: true });
  }, [navigate]);

  const handleTeamBlueprintInstalled = useCallback((teamBlueprint: WorkspaceTeamBlueprint) => {
    setOnboarding((current) => current ? { ...current, teamBlueprint } : current);
    navigate(AUTHENTICATED_DEFAULT_PATH, { replace: true });
  }, [navigate]);

  // One app-level socket per tab that stays open whichever surface is showing:
  // it carries the UI-context reports @yagni grounds on. Events arriving on it
  // are read where they matter, by the surfaces that subscribe to them.
  const { send: sendGlobalWs } = useWebSocket(ignoreAppSocketEvents);

  useReportTimezone(authState === "signed-in");
  useUiContextReporter(sendGlobalWs, authState === "signed-in");
  // The two doors, once, for a workspace with no Team and no GitHub yet.
  useWelcomeRedirect(authState === "signed-in" ? session : null);

  const setFeedbackOpenPersisted = useCallback((next: boolean) => {
    setFeedbackOpen(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("yagni:feedback-panel:open", String(next));
    }
  }, []);

  if (authState === "loading") {
    return (
      <div className="h-full grid place-items-center text-[var(--text-tertiary)] text-sm">
        Loading...
      </div>
    );
  }

  if (authState === "signed-out" || !session) {
    const returnTo = `${location.pathname}${location.search}`;
    // Router navigation, not window.location.href: /auth lives in the same
    // BrowserRouter, and a location assignment during render fires once per
    // render — StrictMode's double render aborts the first document request
    // mid-flight (net::ERR_ABORTED races in the auth-boundary e2e spec).
    return (
      <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />
    );
  }

  const currentRole = activeRole;

  if (isNewWorkspaceFlow) {
    // The "+ new workspace" flow: a plain create form (sales-led onboarding).
    // Once the workspace exists the user lands on the two doors (Connect
    // GitHub, or install YAGNI Code).
    return (
      <CreateWorkspacePage
        onCreated={handleWorkspaceCreated}
        refreshSession={loadSession}
      />
    );
  }

  // A fresh owner is NEVER auto-held in a setup flow: they land on Work (or
  // the two doors, once, while nothing is connected). The create form stays
  // reachable explicitly via `?new=true` (isNewWorkspaceFlow
  // above); invitees still get their intro below.
  // God-mode viewers are never invitees: the intro's introCompleted PATCH is
  // rejected by the read-only write guard, which would strand the admin on the
  // intro screen.
  if (onboarding && !onboarding.introCompleted && currentRole !== "owner" && !session.godMode) {
    return (
      <InviteOnboarding
        onComplete={handleInviteOnboardingComplete}
        userName={session.user.displayName}
        workspaceName={session.workspace.name}
      />
    );
  }

  const showCompanyTeamReview = shouldShowCompanyTeamOnboarding(
    location.pathname,
    currentRole,
    Boolean(session.godMode),
    onboarding?.teamBlueprint,
    true,
  );

  // Unified product shell wraps every signed-in route. AppShell owns the
  // rail, the center with the Ask dock, and the sheet; we render the
  // GodModeBanner and the FeedbackSidePanel as siblings of the routed
  // <Outlet />. Toast portals to the body.
  return (
    <TooltipProvider delayDuration={300}>
      <AppShell
        session={session}
        onSignOut={() => void handleSignOut()}
        onOpenFeedback={() => setFeedbackOpenPersisted(true)}
      >
        {session.godMode && (
          <GodModeBanner
            workspaceName={session.godMode.workspaceName}
            writeEnabled={session.godMode.writeEnabled}
            onToggleWrite={(enabled) => void handleGodModeToggleWrite(enabled)}
            toggling={godModeToggling}
            onExit={() => void handleGodModeExit()}
            exiting={godModeExiting}
          />
        )}
        <PageTransition>
          {showCompanyTeamReview && onboarding?.teamBlueprint ? (
            <CompanyTeamOnboarding
              initialBlueprint={onboarding.teamBlueprint}
              onBlueprintChange={handleTeamBlueprintChange}
              onDeferred={handleTeamBlueprintDeferred}
              onInstalled={handleTeamBlueprintInstalled}
            />
          ) : (
            <Outlet context={{ session }} />
          )}
        </PageTransition>
        {feedbackOpen && resolveFeatureFlag(session, "feedback.panel.enabled") && (
          <FeedbackSidePanel
            session={session}
            onClose={() => setFeedbackOpenPersisted(false)}
          />
        )}
      </AppShell>
      <Toaster position="bottom-right" />
    </TooltipProvider>
  );
}
